import { useState, type CSSProperties } from 'react';
import { Icon } from './Icon';
import { copy, type Language } from '../content';
import { useScrollProgress } from '../motion/useScrollProgress';

/** FDI numbers whose sprites ship with the site, ordered so the ring alternates arches. */
const TEETH = ['11', '12', '13', '14', '16', '21', '23', '25', '26', '31', '33', '36', '41', '44', '46', '47'];

const UNITS = [14, 6, 8, 4, 10, 12, 3, 6];

type ToothOrbitProps = { language: Language };

/**
 * A true 3D cylinder of the product's own tooth sprites: each node is rotated into place
 * around the Y axis and pushed out along Z, so the ring has real depth rather than a
 * scaled-and-faded 2D carousel. Scroll drives the ring angle; hover promotes one tooth.
 */
export function ToothOrbit({ language }: ToothOrbitProps) {
  const text = copy[language].chart;
  const [active, setActive] = useState(0);
  const stageRef = useScrollProgress<HTMLDivElement>();

  const tooth = TEETH[active];
  const restoration = text.restorations[active % text.restorations.length];
  const units = UNITS[active % UNITS.length];

  return (
    <div className="tooth-orbit" ref={stageRef}>
      <div className="tooth-orbit__scroll">
        <div className="tooth-orbit__ring" style={{ '--count': TEETH.length } as CSSProperties}>
          {TEETH.map((number, index) => (
            <button
              type="button"
              key={number}
              className={`tooth-orbit__node ${index === active ? 'is-active' : ''}`}
              style={{ '--i': index } as CSSProperties}
              onPointerEnter={() => setActive(index)}
              onFocus={() => setActive(index)}
              aria-label={`${language === 'ar' ? 'السن' : 'Tooth'} ${number}`}
            >
              <img src={`/media/teeth/${number}.webp`} alt="" width={118} height={138} loading="lazy" decoding="async" />
              <b>{number}</b>
            </button>
          ))}
        </div>
      </div>

      <div className="tooth-orbit__core">
        <span className="tooth-orbit__core-number">{tooth}</span>
        <strong>{restoration}</strong>
        <small>{units} {text.unitLabel}</small>
        <span className="tooth-orbit__hint"><Icon name="spark" />{text.hint}</span>
      </div>
    </div>
  );
}
